import { Calendar, CheckCircle, Eye, Pin, ShoppingCart, Tag, UserCircle2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { BookingStep, StepDefinition, WidgetConfiguration } from './booking-types';

// Import the flow configuration file
// We're assuming this path will work - adjust as needed for your project structure
/**
 * Custom hook to build step configurations from flow configuration
 */
export const useStepBuilder = (widgetConfiguration: WidgetConfiguration | null, bookingPageConfig: any) => {
  const [steps, setSteps] = useState<StepDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation(); // ADD THIS LINE

  // Define node types mapping to booking steps
  const nodeTypeToStep: Record<string, BookingStep> = {
    'locationNode': BookingStep.LOCATION_STEP,
    'categoriesNode': BookingStep.CATEGORIES_STEP,
    'servicesNode': BookingStep.SERVICE_SELECTION,
    'staffMembersNode': BookingStep.PROVIDER_STEP,
    'dateTimeNode': BookingStep.DATE_TIME_SELECTION,
    'customerInfoNode': BookingStep.CUSTOMER_INFO,
    'previewNode': BookingStep.CONFIRMATION,
    'confirmationNode': BookingStep.BOOKING_CONFIRMED
  };

  // Node type mapping to display names - UPDATED WITH t() CALLS
  const nodeTypeToName: Record<string, string> = {
    'locationNode': t('booking.steps.location'), // 'Location'
    'categoriesNode': t('booking.steps.categories'), // 'Categories' - Note: This key doesn't exist yet in your JSON
    'servicesNode': t('booking.steps.service'), // 'Services'
    'staffMembersNode': t('booking.steps.provider'), // 'Staff Member'
    'dateTimeNode': t('booking.steps.datetime'), // 'Date & Time'
    'customerInfoNode': t('booking.steps.customerInfo'), // 'Customer Info' - Note: This key doesn't exist yet
    'previewNode': t('booking.steps.preview'), // 'Preview' - Note: This key doesn't exist yet
    'confirmationNode': t('booking.steps.confirmation') // 'Confirmation'
  };

  // Node type mapping to icons (no changes needed here - icons are JSX elements)
  const nodeTypeToIcon: Record<string, JSX.Element> = {
    'locationNode': <Pin />,
    'categoriesNode': <Tag />,
    'servicesNode': <ShoppingCart />,
    'staffMembersNode': <Users />,
    'dateTimeNode': <Calendar />,
    'customerInfoNode': <UserCircle2 />,
    'previewNode': <Eye />,
    'confirmationNode': <CheckCircle />
  };

  // Helper function to build default steps when no flow is available
  const buildDefaultSteps = (): StepDefinition[] => {
    // Default flow: services -> datetime -> customerInfo -> preview -> confirmation
    const defaultSteps: StepDefinition[] = [
      {
        name: t('booking.steps.service'), // 'Services'
        step: BookingStep.SERVICE_SELECTION,
        icon: <ShoppingCart />,
        nodeData: { settings: {} }
      },
      {
        name: t('booking.steps.datetime'), // 'Date & Time'
        step: BookingStep.DATE_TIME_SELECTION,
        icon: <Calendar />,
        nodeData: { settings: {} }
      },
      {
        name: t('booking.steps.yourInfo'), // 'Your Info' - Note: This key doesn't exist yet
        step: BookingStep.CUSTOMER_INFO,
        icon: <UserCircle2 />,
        nodeData: { settings: {} }
      },
      {
        name: t('booking.steps.preview'), // 'Preview' - Note: This key doesn't exist yet
        step: BookingStep.CONFIRMATION,
        icon: <Eye />,
        nodeData: { settings: {} }
      },
      {
        name: t('booking.steps.confirmation'), // 'Confirmation'
        step: BookingStep.BOOKING_CONFIRMED,
        icon: <CheckCircle />,
        nodeData: { settings: {} }
      },
    ];

    // Add Location step at the beginning if activated
    if (widgetConfiguration?.isLocationsActivated) {
      defaultSteps.unshift({
        name: t('booking.steps.location'), // 'Location'
        step: BookingStep.LOCATION_STEP,
        icon: <Pin />,
        nodeData: null
      });
    }

    // Insert Provider step after Services if activated
    if (widgetConfiguration?.isProvidersActivated) {
      const serviceIndex = defaultSteps.findIndex(s => s.step === BookingStep.SERVICE_SELECTION);
      if (serviceIndex !== -1) {
        defaultSteps.splice(serviceIndex + 1, 0,
          {
            name: t('booking.steps.provider'), // 'Provider'
            step: BookingStep.PROVIDER_STEP,
            icon: <Users />,
            nodeData: null
          }
        );
      }
    }

    return defaultSteps;
  };

  // Helper function to build steps from flow configuration
  const buildStepsFromFlow = (flow: any): StepDefinition[] => {
    try {
      if (!flow || !flow.nodes || !flow.edges) {
        console.warn(t('errors.invalidFlowConfig')); // 'Invalid flow configuration'
        return buildDefaultSteps();
      }

      // Create a map of node IDs to their data
      const nodeMap = flow.nodes.reduce((acc: any, node: any) => {
        acc[node.id] = node;
        return acc;
      }, {});

      // Create an adjacency list for the graph
      const graph: Record<string, string[]> = {};
      flow.edges.forEach((edge: any) => {
        if (!graph[edge.source]) {
          graph[edge.source] = [];
        }
        graph[edge.source].push(edge.target);
      });

      // Find starting node (no incoming edges)
      const incomingEdges = new Set(flow.edges.map((edge: any) => edge.target));
      const startingNodeId = flow.nodes.find((node: any) => !incomingEdges.has(node.id))?.id;

      if (!startingNodeId) {
        console.warn(t('errors.cannotDetermineStartNode')); // 'Could not determine starting node in flow configuration'
        return buildDefaultSteps();
      }

      // Use a BFS (Breadth-First Search) traversal to maintain the correct order
      const orderedNodes: any[] = [];
      const visited = new Set<string>();
      const queue: string[] = [startingNodeId];

      while (queue.length > 0) {
        const nodeId = queue.shift()!;

        if (visited.has(nodeId)) {
          continue;
        }

        visited.add(nodeId);
        orderedNodes.push(nodeMap[nodeId]);

        // Add all neighbors to the queue
        const neighbors = graph[nodeId] || [];
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) {
            queue.push(neighbor);
          }
        }
      }

      // Map nodes to steps
      const mappedSteps = orderedNodes.map(node => {
        const stepType = nodeTypeToStep[node.type];

        // Skip steps that aren't activated based on widget configuration
        if (
          (stepType === BookingStep.LOCATION_STEP && !widgetConfiguration?.isLocationsActivated) ||
          (stepType === BookingStep.PROVIDER_STEP && !widgetConfiguration?.isProvidersActivated)
        ) {
          return null;
        }

        return {
          name: nodeTypeToName[node.type] || node.data.label,
          step: stepType,
          icon: nodeTypeToIcon[node.type] || <ShoppingCart />,
          nodeData: node.data // Include the node data
        };
      }).filter(Boolean) as StepDefinition[];

      // If no valid steps were found, return default steps
      if (mappedSteps.length === 0) {
        return buildDefaultSteps();
      }

      return mappedSteps;
    } catch (error) {
      console.error(t('errors.processingFlowError'), error); // 'Error processing flow configuration:'
      return buildDefaultSteps();
    }
  };

  useEffect(() => {
    if (!widgetConfiguration) {
      setIsLoading(true);
      return;
    }

    const loadFlowConfig = async () => {
      try {
        // Use the imported flow configuration
        // In a real application, this would be fetched from an API
        const stepsFromFlow = buildStepsFromFlow(bookingPageConfig?.configuration);
        setSteps(stepsFromFlow);
      } catch (error) {
        console.error(t('errors.loadingFlowError'), error); // 'Error loading flow configuration:'
        // Fall back to default steps
        setSteps(buildDefaultSteps());
      } finally {
        setIsLoading(false);
      }
    };

    loadFlowConfig();
  }, [bookingPageConfig]);

  // For future: function to fetch steps from API
  const fetchStepsFromBackend = async () => {
    try {
      setIsLoading(true);

      // In a real implementation, this would be a fetch call
      const flowData = bookingPageConfig?.configuration;

      // Build steps from the flow data
      const stepsFromFlow = buildStepsFromFlow(flowData);
      setSteps(stepsFromFlow);
    } catch (error) {
      console.error(t('errors.fetchingFlowError'), error); // 'Error fetching flow configuration:'
      // Fall back to default steps
      setSteps(buildDefaultSteps());
    } finally {
      setIsLoading(false);
    }
  };

  return {
    steps,
    isLoading,
    // Expose the fetch function for manual triggering if needed
    refreshSteps: fetchStepsFromBackend
  };
};

export default useStepBuilder;
